import {
  BooleanInput,
  SimpleForm,
  TextInput,
  required,
  RadioButtonGroupInput,
  useRecordContext,
  FormDataConsumer,
  SaveButton,
  Toolbar,
  ToolbarProps,
  SelectInput,
  NumberInput,
} from 'react-admin';
import {
  FormDivider,
  FormHeader,
  FormSectionHeader,
  DateInputGroup,
} from './form-components';
import { AutocompleteInput, TargetListAutocompleteInput } from './shared';
import { validateContentId } from './form-utils';

const driverContentTypeChoices = [
  { id: 'chatbot', name: 'Chatbot' },
  { id: 'bragBar', name: 'Brag Bar' },
];

const targetingTypeChoices = [
  { id: 'topic_tag', name: 'Content Topic Tag' },
  { id: 'target_list', name: 'Target List' },
  { id: 'run_of_specialty', name: 'Run of Specialty' },
];

const EndemicDriverToolbar = (props: ToolbarProps) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

export const EndemicDriverForm = ({ type, ...rest }: any) => {
  const record = useRecordContext();

  // Extract target list IDs from the expanded targetListsList
  const targetListIdsList = record?.targetListsList?.map((tl: any) => tl.id) || [];
  const specialtyIdsList = record?.specialtyIdsList || [];
  const professionIdsList = record?.professionIdsList || [];

  // Convert topicTagIds array to string for the text input
  const topicTagIdsString = record?.topicTagIds && Array.isArray(record.topicTagIds)
    ? record.topicTagIds.join('\n')
    : '';

  // Determine targeting type based on existing data
  let targetingType = 'topic_tag';
  if (record) {
    if (targetListIdsList.length > 0) {
      targetingType = 'target_list';
    } else if (specialtyIdsList.length > 0 || professionIdsList.length > 0) {
      targetingType = 'run_of_specialty';
    } else if (record.topicTagIds && record.topicTagIds.length > 0) {
      targetingType = 'topic_tag';
    }
  }

  const defaultValues = {
    ...rest.defaultValues,
    isApproved: false,
    priority: 5,
    driverContentType: 'chatbot',
    targetingType: 'topic_tag',
    specialtyIdsList: [],
    professionIdsList: [],
  };

  // For edit mode, override with actual values from record
  const formValues = type === 'Edit' && record ? {
    ...record,
    targetListIdsList,
    specialtyIdsList,
    professionIdsList,
    topicTagIds: topicTagIdsString,
    targetingType,
  } : defaultValues;

  return (
    <SimpleForm
      {...rest}
      defaultValues={formValues}
      toolbar={<EndemicDriverToolbar />}
      mode="onBlur"
      reValidateMode="onBlur"
    >
      <FormHeader>{`${type} Endemic Driver`}</FormHeader>

      <DriverDetailsSection />
      <FormDivider />

      <TargetingSection />
      <FormDivider />

      <ApprovalSection />
    </SimpleForm>
  );
};

const DriverDetailsSection = () => (
  <>
    <FormSectionHeader>Driver Details</FormSectionHeader>
    <SelectInput
      source="driverContentType"
      choices={driverContentTypeChoices}
      validate={required()}
      label="Driver Type"
    />
    <TextInput label="Name" source="name" resettable fullWidth validate={required()} />
    <TextInput
      label="Contentful ID"
      source="contentfulId"
      fullWidth
      resettable
      validate={[required(), validateContentId]}
      helperText="Paste the Contentful entry ID for the chatbot or brag bar"
    />
    <NumberInput
      source="priority"
      label="Priority (1-10)"
      min={1}
      max={10}
      validate={[required(), validatePriority]}
      helperText="1 = highest priority, 10 = lowest priority"
    />
  </>
);

const TargetingSection = () => (
  <>
    <FormSectionHeader>Targeting</FormSectionHeader>
    <RadioButtonGroupInput
      source="targetingType"
      choices={targetingTypeChoices}
      defaultValue="topic_tag"
      validate={required()}
    />

    <FormDataConsumerWithTargeting />
  </>
);

const FormDataConsumerWithTargeting = () => {
  const record = useRecordContext();

  return (
    <FormDataConsumer>
      {({ formData }) => {
        const targetingType = formData.targetingType;

        if (targetingType === 'topic_tag') {
          // For display: if topicTagIds is already a string, use it; if array, convert to string
          const displayValue = formData.topicTagIds
            ? (typeof formData.topicTagIds === 'string'
                ? formData.topicTagIds
                : formData.topicTagIds.join('\n'))
            : '';

          return (
            <TextInput
              label="Topic Tag IDs"
              source="topicTagIds"
              fullWidth
              multiline
              rows={3}
              validate={validateTopicTagIds}
              helperText="Enter Contentful topic tag IDs, one per line or comma-separated"
              format={(value) => {
                if (!value) return '';
                if (typeof value === 'string') return value;
                if (Array.isArray(value)) return value.join('\n');
                return '';
              }}
            />
          );
        }

        if (targetingType === 'target_list') {
          return (
            <TargetListAutocompleteInput
              source="targetListIdsList"
              validate={validateTargetListIds}
            />
          );
        }

        if (targetingType === 'run_of_specialty') {
          return (
            <>
              <AutocompleteInput resource="specialties" source="specialtyIdsList" validate={validateRunOfSpecialty} />
              <AutocompleteInput resource="professions" source="professionIdsList" />
            </>
          );
        }

        return null;
      }}
    </FormDataConsumer>
  );
};

const ApprovalSection = () => {
  const now = new Date();
  const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

  return (
    <>
      <FormSectionHeader>Approval & Scheduling</FormSectionHeader>
      <BooleanInput source="isApproved" label="Approved" defaultValue={false} />
      <DateInputGroup minStartsAt={now} maxEndsAt={oneYearFromNow} />
    </>
  );
};

const validatePriority = (value: number) => {
  if (value < 1 || value > 10) {
    return 'Priority must be between 1 and 10';
  }
  return undefined;
};

const validateTopicTagIds = (value: any) => {
  // Handle non-string values
  if (typeof value !== 'string') {
    return 'Topic tag IDs must be text';
  }

  if (!value || value.trim() === '') {
    return 'Topic tag IDs are required for topic tag targeting';
  }

  // Basic validation - remove spaces and check for valid characters
  const cleanedIds = value.replace(/\s/g, '').split(/[,\n]/).filter(id => id.length > 0);

  // Check for valid Contentful ID format (alphanumeric with hyphens)
  const validIdPattern = /^[a-zA-Z0-9-_]+$/;
  const invalidIds = cleanedIds.filter(id => !validIdPattern.test(id));

  if (invalidIds.length > 0) {
    return `Invalid topic tag IDs: ${invalidIds.join(', ')}. IDs should contain only letters, numbers, hyphens, and underscores.`;
  }

  return undefined;
};

const validateTargetListIds = (value: any, allValues: any) => {
  const targetingType = allValues.targetingType;

  if (targetingType === 'target_list' && (!value || value.length === 0)) {
    return 'At least one Target List is required when using target list targeting';
  }

  return undefined;
};

const validateRunOfSpecialty = (value: any, allValues: any) => {
  if (
    allValues.targetingType === 'run_of_specialty' &&
    (!value || value.length === 0) &&
    (!allValues.professionIdsList || allValues.professionIdsList.length === 0)
  ) {
    return 'At least one specialty or profession is required when using Run of Specialty targeting';
  }
  return undefined;
};
